import { useHealthCheck } from '@/hooks/useHealthCheck';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useTaskStream } from '@/hooks/useTaskStream';
import type { TaskData } from '@/lib/api';
import { TaskMapProvider } from '@/lib/task-context';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { CardTooltip } from './CardTooltip';
import { CardLookupOverlay } from './CardLookupOverlay';
import { KeyboardHelpOverlay } from './KeyboardHelpOverlay';
import { DeckSwitcher } from './DeckSwitcher';
import { ErrorBoundary } from './ErrorBoundary';
import { NavBar } from './NavBar';

// Register service worker as side-effect
import '@/lib/sw-register';
import { toast } from '@/lib/toast';

export function AppLayout() {
	const location = useLocation();
	const isPlaytest = location.pathname.includes('/playtest');
	const [tasks, setTasks] = useState<Map<string, TaskData>>(new Map());
	const [deckSwitcherOpen, setDeckSwitcherOpen] = useState(false);
	const [cardLookupOpen, setCardLookupOpen] = useState(false);
	const [cardLookupInitial, setCardLookupInitial] = useState<string | undefined>(undefined);
	const [keyboardHelpOpen, setKeyboardHelpOpen] = useState(false);
	const navRef = useRef<HTMLDivElement>(null);

	const handleTasksChange = useCallback((updated: Map<string, TaskData>) => {
		setTasks(updated);
	}, []);

	const handleTaskComplete = useCallback((task: TaskData) => {
		// A failed task finishes too — it must not be announced as a success.
		if (task.phase === 'failed') {
			const what = task.task_type === 'system'
				? `${task.phase_label} failed`
				: `${task.deck_name ?? 'Deck'}: ${task.phase_label}`;
			toast.error(`${what}${task.error ? ` — ${task.error}` : ''}`);
			return;
		}
		if (task.task_type === 'system') {
			toast.success(`${task.phase_label} complete`);
			return;
		}
		const name = task.deck_name ?? 'Deck';
		const isDas = task.deck_id?.startsWith('das:');
		toast.success(`${isDas ? 'Dec-a-Sketch' : name} analysis complete`);
	}, []);

	const { connected } = useTaskStream(handleTasksChange, handleTaskComplete);
	useKeyboardShortcuts();
	useHealthCheck();

	useEffect(() => {
		const open = () => setDeckSwitcherOpen(true);
		window.addEventListener('open-deck-switcher', open);
		return () => window.removeEventListener('open-deck-switcher', open);
	}, []);

	useEffect(() => {
		const open = (e: Event) => {
			const cardName = (e as CustomEvent<{ cardName?: string }>).detail?.cardName;
			setCardLookupInitial(cardName);
			setCardLookupOpen(true);
		};
		window.addEventListener('open-card-lookup', open);
		return () => window.removeEventListener('open-card-lookup', open);
	}, []);

	// Double-click on card names (desktop) and long-press (mobile) → open lookup
	useEffect(() => {
		function getCardName(target: EventTarget | null): string | null {
			const el = (target as Element | null)?.closest?.('[data-card-name]');
			return el?.getAttribute('data-card-name') ?? null;
		}

		function onDblClick(e: MouseEvent) {
			const name = getCardName(e.target);
			if (!name) return;
			e.preventDefault();
			window.dispatchEvent(new CustomEvent('open-card-lookup', { detail: { cardName: name } }));
		}

		let longPressTimer: ReturnType<typeof setTimeout> | null = null;
		let longPressTarget: string | null = null;

		function onTouchStart(e: TouchEvent) {
			const name = getCardName(e.target);
			if (!name) return;
			longPressTarget = name;
			longPressTimer = setTimeout(() => {
				if (longPressTarget) {
					window.dispatchEvent(new CustomEvent('open-card-lookup', { detail: { cardName: longPressTarget } }));
				}
			}, 600);
		}

		function cancelLongPress() {
			if (longPressTimer) {
				clearTimeout(longPressTimer);
				longPressTimer = null;
			}
			longPressTarget = null;
		}

		document.addEventListener('dblclick', onDblClick);
		document.addEventListener('touchstart', onTouchStart, { passive: true });
		document.addEventListener('touchend', cancelLongPress);
		document.addEventListener('touchmove', cancelLongPress, { passive: true });

		return () => {
			document.removeEventListener('dblclick', onDblClick);
			document.removeEventListener('touchstart', onTouchStart);
			document.removeEventListener('touchend', cancelLongPress);
			document.removeEventListener('touchmove', cancelLongPress);
		};
	}, []);

	useEffect(() => {
		const open = () => setKeyboardHelpOpen(true);
		window.addEventListener('open-keyboard-help', open);
		return () => window.removeEventListener('open-keyboard-help', open);
	}, []);

	useEffect(() => {
		const el = navRef.current;
		if (!el) return;
		const ro = new ResizeObserver(([entry]) => {
			document.documentElement.style.setProperty('--nav-height', `${entry.contentRect.height}px`);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	return (
		<TaskMapProvider value={tasks}>
			<div
				ref={navRef}
				className="fixed top-0 left-0 right-0 z-[1000]"
				id="top-nav"
			>
				<NavBar tasks={tasks} sseConnected={connected} />
			</div>

			{/* Main content — pushed below fixed nav */}
			<main
				id="app-view"
				className={`pt-nav-height${isPlaytest ? '' : ' min-h-screen'}`}
			>
				<ErrorBoundary>
					<Suspense fallback={<div className="p-12 text-center text-on-surface-variant">Loading…</div>}>
						<Outlet />
					</Suspense>
				</ErrorBoundary>
			</main>

			{/* Site footer — hidden on playtester */}
			{!isPlaytest && <footer
				className="flex items-center justify-center flex-wrap gap-spacing-sm px-spacing-lg py-spacing-md mt-spacing-xl border-t border-outline-variant text-sm text-on-surface-variant"
			>
				<span>&copy; 2026 Benjamin Saller</span>
				<span className="opacity-50">&bull;</span>
				<a href="/static/about.html" target="_blank" rel="noopener" className="text-on-surface-variant">
					About
				</a>

				<span className="opacity-50">&bull;</span>
				<a href="/static/search-syntax.html" target="_blank" rel="noopener" className="text-on-surface-variant">
					Search &amp; Filter Syntax
				</a>
				<span className="opacity-50">&bull;</span>
				<a href="/static/privacy.html" target="_blank" rel="noopener" className="text-on-surface-variant">
					Privacy Policy
				</a>
				<div
					className="basis-full max-w-[600px] text-center mt-spacing-sm italic text-[0.8em]"
				>Wizards of the Coast, Magic: The Gathering, and their logos are trademarks of Wizards of the Coast LLC in the United States and other countries. © 1993-2026 Wizards. All Rights Reserved.

					Data is collected from a variety of sources. These include EDHRec, Scryfall, EDHTop16,
					Moxfield, Archidekt, Commander Spellbook, and other Magic: The Gathering databases.
					A special thanks to all of them without which this would not be possible. Recommendations and
					analysis are provided on a best-effort basis and may not always be accurate.
					Use at your own discretion.
				</div>
			</footer>}

			{/* Global card image tooltip */}
			<CardTooltip />

			{/* Global deck switcher (press 'g') */}
			<DeckSwitcher open={deckSwitcherOpen} onClose={() => setDeckSwitcherOpen(false)} />

			{/* Global card lookup overlay (press 'l', dblclick, or long-press) */}
			<CardLookupOverlay
				open={cardLookupOpen}
				initialCard={cardLookupInitial}
				onClose={() => { setCardLookupOpen(false); setCardLookupInitial(undefined); }}
			/>

			{/* Keyboard shortcuts help overlay (press '?') */}
			<KeyboardHelpOverlay open={keyboardHelpOpen} onClose={() => setKeyboardHelpOpen(false)} />

		</TaskMapProvider>
	);
}
